import { useState } from "react";
import {
  IconBell,
  IconMail,
  IconShieldLock,
  IconReceipt,
  IconTruck,
  IconUserCheck,
  IconSpeakerphone,
  IconKey,
  IconFileInvoice,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Switch } from "../../../../components/common/switch";
import { Shimmer, ShimmerLoader } from "../../../../components/common/shimmer";
import { notificationsService } from "../../../api_client";
import type { NotificationPrefsDto, UpdateNotificationPrefsDto } from "../../../../api";
import { showErrorToast } from "../../../../utils/toasts";
import { usePushPreference } from "../../../../hooks/use-push-preference";
import { useNotificationPrefs } from "../../hooks/useNotificationPrefs";

type ToggleField = keyof NotificationPrefsDto;

interface ToggleRow {
  field: ToggleField;
  icon: typeof IconTruck;
  title: string;
  description: string;
}

const TOGGLES: ToggleRow[] = [
  {
    field: "emailOrders",
    icon: IconTruck,
    title: "Order updates",
    description:
      "Confirmations, shipping updates, delivery notifications, and cancellations.",
  },
  {
    field: "emailAccount",
    icon: IconUserCheck,
    title: "Account activity",
    description:
      "KYC document decisions, partner approval, and outcomes for your change and product requests.",
  },
  {
    field: "emailMarketing",
    icon: IconSpeakerphone,
    title: "Promotions & news",
    description: "Occasional offers, product updates, and platform announcements.",
  },
];

interface AlwaysOnRow {
  icon: typeof IconShieldLock;
  title: string;
  description: string;
}

const ALWAYS_ON: AlwaysOnRow[] = [
  {
    icon: IconKey,
    title: "Security",
    description:
      "Login alerts, one-time codes, password resets, and email or phone changes.",
  },
  {
    icon: IconFileInvoice,
    title: "Billing & legal",
    description:
      "Invoices, payment receipts, refunds, tax documents, and updates to terms of service.",
  },
];

export function NotificationsSection() {
  // No client-side defaults: a switch is only drawn once the saved value is
  // known, so a reload never shows the opposite state first.
  const { data: prefs, error: prefsError, mutate: mutatePrefs } = useNotificationPrefs();
  const [pushEnabled, setPushEnabled] = usePushPreference();
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  async function handlePushToggle(v: boolean) {
    if (v && pushPermission !== "granted") {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== "granted") return;
    }
    setPushEnabled(v);
  }

  // Each toggle dispatches its own PATCH so the user gets per-row feedback
  // and one failure doesn't roll back unrelated toggles.
  const updateField = async (field: ToggleField, value: boolean) => {
    if (!prefs) return;
    const previous = prefs;
    await mutatePrefs({ ...prefs, [field]: value }, { revalidate: false });
    try {
      const res = await notificationsService.updateMyNotificationPrefs({
        [field]: value,
      } as UpdateNotificationPrefsDto);
      await mutatePrefs(res.data, { revalidate: false });
    } catch (err) {
      await mutatePrefs(previous, { revalidate: false });
      showErrorToast("Failed to update notification preferences", err);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-center gap-4 px-6 pt-5 pb-3 max-md:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
            <IconMail size={20} className="text-textMuted" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Email notifications</Heading>
            <Text variant="bodySm" tone="dark">
              Choose which non-essential emails you'd like to receive. In-app notifications stay on for everything.
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pb-5 max-md:px-4">
          {!prefs && !prefsError && (
            <ShimmerLoader message="Loading your email preferences…">
              {TOGGLES.map(({ field }) => (
                <Shimmer key={field} className="h-[74px] w-full rounded-2xl" />
              ))}
            </ShimmerLoader>
          )}
          {prefsError && (
            <Text variant="bodySm" tone="inherit" className="text-accentError">
              Couldn't load your email preferences. Please refresh the page.
            </Text>
          )}
          {prefs && TOGGLES.map(({ field, icon: Icon, title, description }) => (
            <div
              key={field}
              className="flex items-center gap-4 rounded-2xl border border-borderDefault px-4 py-4 max-md:gap-3 max-md:px-3"
            >
              <div className="flex h-10 w-10 shrink-0 max-md:hidden items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
                <Icon size={20} className="text-textMuted" />
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                <Text variant="bodyBold" tone="dark">{title}</Text>
                <Text variant="bodySm" tone="dark">{description}</Text>
              </div>
              <Switch
                checked={prefs[field]}
                onChange={(v) => updateField(field, v)}
                aria-label={title}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-center gap-4 px-6 pt-5 pb-3 max-md:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
            <IconBell size={20} className="text-textMuted" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Browser notifications</Heading>
            <Text variant="bodySm" tone="dark">
              Receive native browser alerts for delivery and shipping events.
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pb-5 max-md:px-4">
          {pushPermission === "denied" && (
            <div className="flex items-start gap-3 rounded-xl border border-borderDefault bg-bgLight p-3">
              <IconAlertTriangle size={18} className="mt-0.5 shrink-0 text-textDark" />
              <Text variant="bodySm" tone="dark">
                Browser notifications are blocked. Allow them in your browser settings to enable this feature.
              </Text>
            </div>
          )}
          <div className="flex items-center gap-4 rounded-2xl border border-borderDefault px-4 py-4 max-md:gap-3 max-md:px-3">
            <div className="flex h-10 w-10 shrink-0 max-md:hidden items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
              <IconTruck size={20} className="text-textMuted" />
            </div>
            <div className="flex flex-1 flex-col gap-0.5">
              <Text variant="bodyBold" tone="dark">Delivery & shipping alerts</Text>
              <Text variant="bodySm" tone="dark">
                Get notified when your order ships, is delivered, or is cancelled.
              </Text>
            </div>
            <Switch
              checked={pushEnabled && pushPermission === "granted"}
              onChange={handlePushToggle}
              disabled={pushPermission === "denied"}
              aria-label="Browser delivery notifications"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-center gap-4 px-6 pt-5 pb-3 max-md:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
            <IconShieldLock size={20} className="text-textMuted" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Always on</Heading>
            <Text variant="bodySm" tone="dark">
              These emails are sent for legal and security reasons and can't be turned off.
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pb-5 max-md:px-4">
          {ALWAYS_ON.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="flex items-center gap-4 rounded-2xl border border-borderDefault bg-bgLight px-4 py-4 max-md:gap-3 max-md:px-3"
            >
              <div className="flex h-10 w-10 shrink-0 max-md:hidden items-center justify-center rounded-xl bg-bgWhite border border-borderDefault">
                <Icon size={20} className="text-textMuted" />
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                <Text variant="bodyBold" tone="dark">{title}</Text>
                <Text variant="bodySm" tone="dark">{description}</Text>
              </div>
              <div className="flex items-center gap-1.5">
                <IconReceipt size={14} className="text-textMuted" />
                <Text variant="bodyXs" tone="muted">Required</Text>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
